import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtNum } from "@/lib/format";
import { AlertTriangle, CheckCircle2, XCircle, ShieldAlert, Search, Fingerprint, FileX } from "lucide-react";
import type { AuditSeverity } from "@/lib/fiscal-types";
import { Input } from "@/components/ui/input";

const SEV_STYLES: Record<AuditSeverity, { bg: string; text: string; ring: string; label: string }> = {
  CRITICA: { bg: "bg-red-500/15", text: "text-red-500", ring: "ring-red-500/30", label: "CRÍTICA" },
  ALTA:    { bg: "bg-orange-500/15", text: "text-orange-500", ring: "ring-orange-500/30", label: "ALTA" },
  MEDIA:   { bg: "bg-amber-500/15", text: "text-amber-500", ring: "ring-amber-500/30", label: "MÉDIA" },
  BAIXA:   { bg: "bg-yellow-500/15", text: "text-yellow-500", ring: "ring-yellow-500/30", label: "BAIXA" },
  INFO:    { bg: "bg-sky-500/15", text: "text-sky-500", ring: "ring-sky-500/30", label: "INFO" },
};

function SevBadge({ s }: { s: AuditSeverity }) {
  const sty = SEV_STYLES[s];
  return (
    <span className={`px-2.5 py-1 rounded-md text-[9px] font-black tracking-widest ring-1 uppercase ${sty.bg} ${sty.text} ${sty.ring}`}>
      {sty.label}
    </span>
  );
}

export default function ValidacaoXmlPage() {
  const { notas, dashboard } = useFiscalStore();
  const [q, setQ] = useState("");
  const [filtroSev, setFiltroSev] = useState<AuditSeverity | "all">("all");

  useEffect(() => { document.title = "Validação de XML — DataGrid XML"; }, []);

  const { validas, comErro, estruturaFindings } = useMemo(() => {
    const CATS_ESTRUTURA = new Set(["ESTRUTURA", "PROTOCOLO", "INTEGRIDADE"]);
    let validas = 0, comErro = 0;
    const estruturaFindings: { nota: typeof notas[0]; findings: typeof notas[0]["auditFindings"] }[] = [];

    for (const nota of notas) {
      const findings = (nota.auditFindings ?? []).filter((f) => CATS_ESTRUTURA.has(f.categoria));
      if (findings.length > 0) {
        comErro++;
        estruturaFindings.push({ nota, findings });
      } else {
        validas++;
      }
    }
    return { validas, comErro, estruturaFindings };
  }, [notas]);

  const filtrados = useMemo(() => {
    const ql = q.toLowerCase();
    return estruturaFindings.filter(({ nota, findings }) => {
      if (filtroSev !== "all" && !findings.some((f) => f.severidade === filtroSev)) return false;
      if (ql) return nota.chave.toLowerCase().includes(ql) || nota.emitente.toLowerCase().includes(ql) || (nota.numero || "").includes(ql);
      return true;
    });
  }, [estruturaFindings, q, filtroSev]);

  const totalFindings = estruturaFindings.reduce((s, { findings }) => s + findings.length, 0);

  // Removido redirecionamento automático para permitir acesso livre conforme solicitado.
  // if (!dashboard && notas.length === 0) return <Navigate to="/upload" replace />;

  return (
    <div className="px-6 py-8 md:px-8 max-w-[1600px] mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 glass-card p-6 border-l-4 border-l-primary">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <ShieldAlert className="size-6 text-primary" />
            </div>
            <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-foreground">Validação de XML</h1>
          </div>
          <p className="text-sm lg:text-base text-muted-foreground font-medium">
            Inspeção técnica de estrutura, protocolo e integridade dos arquivos importados.
          </p>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid md:grid-cols-4 gap-6">
        {[
          { label: "XMLs válidos", value: fmtNum(validas), icon: CheckCircle2, bg: "bg-emerald-500/10", border: "border-emerald-500/20", textColor: "text-emerald-500", delay: '100ms' },
          { label: "Com problemas", value: fmtNum(comErro), icon: XCircle, bg: "bg-red-500/10", border: "border-red-500/20", textColor: "text-red-500", delay: '200ms' },
          { label: "Total de achados", value: fmtNum(totalFindings), icon: ShieldAlert, bg: "bg-amber-500/10", border: "border-amber-500/20", textColor: "text-amber-500", delay: '300ms' },
          { label: "Total processados", value: fmtNum(notas.length), icon: Fingerprint, bg: "bg-blue-500/10", border: "border-blue-500/20", textColor: "text-blue-500", delay: '400ms' },
        ].map((kpi) => (
          <div key={kpi.label} className={`glass-card group relative overflow-hidden rounded-3xl p-6 border ${kpi.border} ${kpi.bg} animate-in-card hover-lift`} style={{ animationDelay: kpi.delay }}>
            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground">{kpi.label}</div>
              <kpi.icon className={`size-5 ${kpi.textColor} opacity-80 group-hover:scale-110 transition-transform`} />
            </div>
            <div className={`text-4xl font-black tabular-nums ${kpi.textColor} relative z-10`}>{kpi.value}</div>
            <div className="absolute -right-6 -bottom-6 opacity-[0.04] group-hover:opacity-[0.08] transition-opacity">
              <kpi.icon className="size-32 rotate-12" />
            </div>
          </div>
        ))}
      </div>

      {/* Banner de status geral */}
      <div className={`glass-card rounded-3xl p-8 flex flex-col md:flex-row items-start md:items-center gap-6 border-2 transition-all duration-500 shadow-xl animate-in-card ${
        comErro === 0 
          ? "bg-emerald-500/5 border-emerald-500/20" 
          : "bg-amber-500/5 border-amber-500/20"
      }`} style={{ animationDelay: '500ms' }}>
        <div className={`size-16 rounded-2xl flex items-center justify-center shrink-0 shadow-inner ${
          comErro === 0 ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-amber-500/10 text-amber-500 border border-amber-500/20"
        }`}>
          {comErro === 0 ? <CheckCircle2 className="size-8" /> : <AlertTriangle className="size-8" />}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className={`text-2xl lg:text-3xl font-black tracking-tight ${comErro === 0 ? "text-emerald-500" : "text-amber-500"}`}>
            {comErro === 0 ? "Nenhum erro de integridade detectado" : `${fmtNum(comErro)} XMLs requerem atenção técnica`}
          </div>
          <div className="text-sm text-muted-foreground font-semibold leading-relaxed">
            {comErro === 0
              ? "Excelente! Todos os arquivos processados estão em conformidade com as regras de estrutura e protocolo da SEFAZ."
              : "Atenção: Foram identificados problemas que podem invalidar ou atrasar o processamento destes documentos. Verifique o detalhamento abaixo."}
          </div>
        </div>
        {comErro > 0 && (
          <div className="px-5 py-2.5 bg-amber-500/10 border-2 border-amber-500/30 rounded-xl text-[11px] font-black text-amber-500 uppercase tracking-[0.2em] shadow-sm animate-pulse whitespace-nowrap">
            Ação Requerida
          </div>
        )}
      </div>

      {/* Filtros */}
      {estruturaFindings.length > 0 && (
        <>
          <div className="glass-card p-4 flex flex-col xl:flex-row gap-4 xl:items-center justify-between animate-in-card" style={{ animationDelay: '600ms' }}>
            <div className="relative w-full xl:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Filtrar por chave, número ou emitente…"
                className="input-premium pl-10 h-11 w-full text-sm" />
            </div>
            <div className="flex flex-wrap rounded-xl border border-border glass p-1 bg-background/30 shadow-inner w-fit">
              {(["all", "CRITICA", "ALTA", "MEDIA", "BAIXA", "INFO"] as const).map((s) => {
                const sty = s === "all" ? null : SEV_STYLES[s];
                const isSelected = filtroSev === s;
                return (
                  <button key={s} onClick={() => setFiltroSev(s)}
                    className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all duration-300 ${
                      isSelected 
                        ? (sty ? `${sty.bg} ${sty.text} shadow-md ring-1 ${sty.ring}` : "bg-primary text-primary-foreground shadow-md") 
                        : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
                    }`}>
                    {s === "all" ? "Todos" : SEV_STYLES[s].label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4">
            {filtrados.map(({ nota, findings }) => (
              <div key={nota.chave} className="group relative glass-card overflow-hidden hover:border-border transition-all duration-300 shadow-sm hover:shadow-md">
                <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/50 bg-background/30">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="size-12 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
                      <FileX className="size-6 text-red-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base font-black text-foreground flex items-center gap-2">
                        <span>NF {nota.numero}/{nota.serie}</span>
                        <span className="text-muted-foreground font-normal">|</span>
                        <span className="truncate opacity-90 font-semibold">{nota.emitente || "(Emitente não identificado)"}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono mt-1 tracking-wider">{nota.chave}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-[10px] font-black text-red-500 uppercase tracking-widest shadow-inner">
                      {findings.length} Incidência(s)
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-3 bg-background/10">
                  {findings.map((f, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-background/40 border border-border/50 hover:bg-background/60 transition-colors">
                      <div className="mt-0.5"><SevBadge s={f.severidade} /></div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-sm">
                          <span className="font-mono text-primary font-bold bg-primary/10 px-2 py-0.5 rounded border border-primary/20 uppercase tracking-wider">{f.regra}</span>
                          <span className="text-foreground font-medium leading-relaxed">{f.mensagem}</span>
                        </div>
                        {f.campo && (
                          <div className="flex items-center gap-2 mt-3">
                            <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">Localização:</span>
                            <span className="font-mono text-[10px] text-muted-foreground bg-background/50 px-2.5 py-1 rounded-md border border-border">{f.campo}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {filtrados.length === 0 && (
              <div className="flex flex-col items-center justify-center py-24 glass-card border-dashed">
                <div className="size-20 rounded-full bg-emerald-500/10 flex items-center justify-center mb-5">
                  <CheckCircle2 className="size-10 text-emerald-500" />
                </div>
                <p className="text-foreground font-bold text-lg">Nenhum resultado encontrado</p>
                <p className="text-muted-foreground text-sm mt-1">Os filtros aplicados não retornaram nenhuma divergência.</p>
                <button onClick={() => { setQ(""); setFiltroSev("all"); }} className="mt-6 text-sm font-bold text-primary hover:text-primary/80 transition-colors underline underline-offset-4">Limpar todos os filtros</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
